/**
 * Lead Create Form Component
 * ===========================
 * Form for creating new leads
 */

import React, { useState, useRef, useEffect } from 'react';
import { leadAPI, proposalAPI } from '../../api';
import { Card, CardHeader, CardBody, Button, Input } from '../ui';

const LeadCreateForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    company: '',
    email: '',
    phone: '',
    website: '',
    linkedin: '',
    notes: '',
    lead_source: '',
    avatar_config_id: '',
  });
  const [errors, setErrors] = useState({});
  const [avatarConfigs, setAvatarConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const errorRef = useRef(null);

  // Scroll to error message when it appears
  useEffect(() => {
    if (errors.submit && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors.submit]);

  useEffect(() => {
    proposalAPI.getAvatarConfigs().then(res => {
      if (res.data) setAvatarConfigs(res.data.filter(c => c.is_active));
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    // Email is optional, but if provided, must be valid
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    // Phone is now optional - no validation required

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      // Filter out empty strings to send null/undefined for optional fields
      const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
        // Only include non-empty values
        if (value && value.trim() !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});

      const response = await leadAPI.createLead(cleanedData);
      // Backend returns {data: {...}, meta: {...}}
      if (response.data) {
        onSuccess && onSuccess(response.data);
      }
    } catch (err) {
      if (err.code === 'VALIDATION_ERROR' && err.details && Object.keys(err.details).length > 0) {
        setErrors({ ...err.details, submit: err.message || 'Please fix the validation errors below' });
      } else {
        setErrors({ submit: err.message || 'Failed to create lead' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <h2 className="text-2xl font-sans font-semibold text-gray-900 dark:text-white">Create New Lead</h2>
      </CardHeader>
      <CardBody>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message */}
          {errors.submit && (
            <div ref={errorRef} className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {errors.submit}
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Smith"
                error={errors.name}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Designation
              </label>
              <Input
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                placeholder="Chief Investment Officer"
                error={errors.designation}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company <span className="text-red-500">*</span>
              </label>
              <Input
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Global Asset Management LLC"
                error={errors.company}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john.smith@company.com"
                error={errors.email}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1-555-0123"
                error={errors.phone}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website
              </label>
              <Input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://company.com"
              />
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                LinkedIn Profile
              </label>
              <Input
                type="url"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/johnsmith"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lead Source
              </label>
              <Input
                name="lead_source"
                value={formData.lead_source}
                onChange={handleChange}
                placeholder="Conference - Dubai Fintech Summit 2026"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Presenter
              </label>
              <select
                name="avatar_config_id"
                value={formData.avatar_config_id}
                onChange={handleChange}
                className="input bg-white"
              >
                <option value="">Select presenter</option>
                {avatarConfigs.map(config => (
                  <option key={config.id} value={config.id}>{config.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary"
                placeholder="Additional notes about this lead..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Lead'
              )}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

export default LeadCreateForm;

